import { AxiosResponse } from 'axios';

import { notificationsInstance } from 'api/base';
import { normalizeGist, normalizePageData } from 'utils/commonNormalizer';
import { normalizeSearchFiltersPayload } from 'utils/filters';
import { IAllPageFilters, IGist, IPageType, TObject } from 'utils/types';

import {
  normalizeCustomMessagesData,
  normalizeNotificationCenterData,
  normalizeUserGroupData,
} from './normalizers';
import {
  ICustomMessageEditPayload,
  ICustomMessageForm,
  ICustomMessages,
  INotificationCenterData,
  IUpdateNotificationStatePayload,
  IUserGroupData,
  IUserGroupEditPayload,
  IUserGroupForm,
  TCustomMessagesData,
} from './types';

const notificationCenterApi = {
  async getAllNotifications(
    filters: IAllPageFilters,
  ): Promise<IPageType<INotificationCenterData>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await notificationsInstance.get('notifications', {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, INotificationCenterData>(
      response.data,
      normalizeNotificationCenterData,
      JSON.stringify(filters.search),
    );
  },

  async updateNotificationState({
    id,
    state,
  }: IUpdateNotificationStatePayload): Promise<AxiosResponse> {
    const response = await notificationsInstance.patch(`notifications/${id}`, null, {
      params: { state },
    });
    return response;
  },

  async getAllUserGroups(
    filters: IAllPageFilters,
  ): Promise<IPageType<Omit<IUserGroupData, 'users'>>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await notificationsInstance.get('user-groups', {
      params: normalizedFilter,
    });

    return normalizePageData<TObject, Omit<IUserGroupData, 'users'>>(
      response.data,
      normalizeUserGroupData,
      JSON.stringify(filters.search),
    );
  },

  async createUserGroup(data: IUserGroupForm): Promise<AxiosResponse> {
    const response = await notificationsInstance.post('user-groups', data);
    return response;
  },

  async getUserGroup(id: number): Promise<IUserGroupData> {
    const response = await notificationsInstance.get(`user-groups/${id}`);
    return normalizeUserGroupData(response.data);
  },

  async updateUserGroup({ id, ...data }: IUserGroupEditPayload): Promise<AxiosResponse> {
    const response = await notificationsInstance.put(`user-groups/${id}`, data);
    return response;
  },

  async deleteUserGroup(id: number): Promise<AxiosResponse> {
    const response = await notificationsInstance.delete(`user-groups/${id}`);
    return response;
  },

  async getUserGroupsList(): Promise<IGist[]> {
    const response = await notificationsInstance.get(`user-groups/descriptions`);

    return response.data?.map(normalizeGist) || [];
  },

  async getAllCustomMessages(
    filters: IAllPageFilters,
  ): Promise<IPageType<ICustomMessages>> {
    const normalizedFilter = normalizeSearchFiltersPayload(filters);

    const response = await notificationsInstance.get('custom-messages', {
      params: normalizedFilter,
    });

    return normalizePageData<TCustomMessagesData, ICustomMessages>(
      response.data,
      normalizeCustomMessagesData,
      JSON.stringify(filters.search),
    );
  },

  async getCustomMessage(id: number): Promise<ICustomMessages> {
    const response = await notificationsInstance.get(`custom-messages/${id}`);
    return normalizeCustomMessagesData(response.data);
  },

  async createCustomMessage(data: ICustomMessageForm): Promise<AxiosResponse> {
    const response = await notificationsInstance.post('custom-messages', data);
    return response;
  },

  async updateCustomMessage({
    id,
    ...data
  }: ICustomMessageEditPayload): Promise<AxiosResponse> {
    const response = await notificationsInstance.put(`custom-messages/${id}`, data);
    return response;
  },

  async deleteCustomMessage(id: number): Promise<AxiosResponse> {
    const response = await notificationsInstance.delete(`custom-messages/${id}`);
    return response;
  },
};

export default notificationCenterApi;
